import { PixelCrop } from 'react-image-crop';

import { Size } from '../../typings/common';
import { CreativeImageCropData } from '../common/typings';
import {
    convertCropToPercentCrop,
    convertCropToPixelCrop,
    getCroppedImageBlob
} from '../CreativeImageCropHelper/CreativeImageCropHelper';

import {
    CreativeImageCenter,
    CreativeImageCropParameter,
    CreativeImageCropSize,
    CreativeImageCropSizesData,
    CreativeImageCropSizesValue,
    ImageCropApiData,
    ImageCropApiDataItem,
    ImageCropRequestData
} from './typings';

const DEFAULT_CROP_SIZE_MODIFIER = 0.8;
// веоятно нужен, чтобы на ретиновых экранах было приемлемое качество
const RETINA_MULTIPLIER = 2;

/* Считает дефолтный кроп изображения в 0.8 ширины или 0.8 высоты */
export const getCreativeImageDefaultCrop = (naturalImageSize: Size, size: CreativeImageCropSize): PixelCrop => {
    let scale = Math.min(naturalImageSize.width / size.width, naturalImageSize.height / size.height);

    if (scale * DEFAULT_CROP_SIZE_MODIFIER > RETINA_MULTIPLIER) {
        scale *= DEFAULT_CROP_SIZE_MODIFIER;
    } else if (scale > RETINA_MULTIPLIER) {
        scale = RETINA_MULTIPLIER;
    }

    const width = size.width * scale;
    const height = size.height * scale;

    return {
        x: (naturalImageSize.width - width) / 2,
        y: (naturalImageSize.height - height) / 2,
        width,
        height
    };
};

/* Считает минимальные и максимальные размеры кропа и корректирует позицию кропа, если был запомнен центр */
export const getCreativeImageCenteredCrop = (
    naturalImageSize: Size,
    size: CreativeImageCropSize,
    suggestedCenter?: CreativeImageCenter
): PixelCrop => {
    const sizeAspect = size.width / size.height;
    const scale = Math.min(naturalImageSize.width / size.width, naturalImageSize.height / size.height);

    const defaultCrop = getCreativeImageDefaultCrop(naturalImageSize, size);

    if (suggestedCenter) {
        defaultCrop.width *= suggestedCenter.scale;
        defaultCrop.height = defaultCrop.width / sizeAspect;

        // если запомненное значение выходит за границы картинки, то нужно скорректировать на максимум от размеров картинки
        if (defaultCrop.width > naturalImageSize.width || defaultCrop.height > naturalImageSize.height) {
            defaultCrop.width = size.width * scale;
            defaultCrop.height = size.height * scale;
        }

        defaultCrop.x = Math.max(suggestedCenter.x - defaultCrop.width / 2, 0);
        defaultCrop.y = Math.max(suggestedCenter.y - defaultCrop.height / 2, 0);

        if (defaultCrop.x + defaultCrop.width > naturalImageSize.width) {
            defaultCrop.x = naturalImageSize.width - defaultCrop.width;
        }

        if (defaultCrop.y + defaultCrop.height > naturalImageSize.height) {
            defaultCrop.y = naturalImageSize.height - defaultCrop.height;
        }
    }

    return defaultCrop;
};

export const getCreativeImageCropSizesValue = (
    cropBoxes?: CreativeImageCropParameter[]
): CreativeImageCropSizesValue | undefined => {
    if (!cropBoxes) {
        return;
    }

    return cropBoxes.reduce((sizesMap: CreativeImageCropSizesValue, size: CreativeImageCropParameter) => {
        sizesMap[size.id] = {
            crop: size
        };

        return sizesMap;
    }, {});
};

export const getCreativeImageCropDataFromPixelCrop = (
    naturalImageSize: Size,
    crop: PixelCrop,
    currentSize: CreativeImageCropSize
): CreativeImageCropData => {
    const aspect = currentSize.width / currentSize.height;
    const minWidth = currentSize.width * RETINA_MULTIPLIER;
    const minHeight = currentSize.height * RETINA_MULTIPLIER;

    let maxWidth = naturalImageSize.width;
    let maxHeight = naturalImageSize.height;

    if (maxHeight * aspect > maxWidth) {
        maxHeight = maxWidth / aspect;
    } else {
        maxWidth = maxHeight * aspect;
    }

    return {
        crop: {
            ...convertCropToPercentCrop(naturalImageSize, crop),
            aspect
        },
        minWidth: (Math.min(minWidth, maxWidth) * 100) / naturalImageSize.width,
        minHeight: (Math.min(minHeight, maxHeight) * 100) / naturalImageSize.height
    };
};

export const getDefaultCenter = (crop: PixelCrop, defaultWidth?: number): CreativeImageCenter => {
    return {
        x: crop.x + crop.width / 2,
        y: crop.y + crop.height / 2,
        scale: defaultWidth ? crop.width / defaultWidth : 1
    };
};

export const getCreativeImagePixelCrop = (
    naturalImageSize: Size,
    currentSize: CreativeImageCropSize,
    cropValue?: CreativeImageCropSizesValue,
    suggestedCenter?: CreativeImageCenter
): PixelCrop => {
    if (!cropValue) {
        return getCreativeImageCenteredCrop(naturalImageSize, currentSize, suggestedCenter);
    }

    return cropValue[currentSize.id].crop;
};

export const getCreativeImageCropData = (
    naturalImageSize: Size,
    currentSize: CreativeImageCropSize,
    cropValue?: CreativeImageCropSizesValue,
    suggestedCenter?: CreativeImageCenter
): CreativeImageCropData => {
    const pixelCrop = getCreativeImagePixelCrop(naturalImageSize, currentSize, cropValue, suggestedCenter);

    return getCreativeImageCropDataFromPixelCrop(naturalImageSize, pixelCrop, currentSize);
};

export const getEditedSizes = (
    cropSizes: CreativeImageCropSize[],
    cropData: CreativeImageCropSizesData,
    isEditMode?: boolean
): CreativeImageCropSize[] => {
    let editedSizes = cropSizes;

    if (isEditMode) {
        editedSizes = editedSizes.filter((size: CreativeImageCropSize) => {
            return Boolean(cropData[size.id]);
        });
    }

    return editedSizes;
};

export function getImageCropBlob(
    size: CreativeImageCropSize,
    image: HTMLImageElement,
    cropData?: CreativeImageCropData,
    suggestedCenter?: CreativeImageCenter
): Promise<ImageCropApiDataItem> {
    const naturalImageSize = {
        width: image.naturalWidth,
        height: image.naturalHeight
    };

    const crop = cropData
        ? convertCropToPixelCrop(cropData.crop, naturalImageSize)
        : getCreativeImagePixelCrop(naturalImageSize, size, undefined, suggestedCenter);

    return getCroppedImageBlob(crop, image, {
        // скейлим изображение к x2 размерам баннера (нужно, вероятно, для ретины)
        width: size.width * RETINA_MULTIPLIER,
        height: size.height * RETINA_MULTIPLIER
    }).then((blob: Blob) => {
        return {
            size: { ...crop, id: size.id },
            blob
        };
    });
}

export async function getCroppedImageBlobList(
    sizes: CreativeImageCropSize[],
    image: HTMLImageElement,
    cropDataMap: CreativeImageCropSizesData,
    suggestedCenter?: CreativeImageCenter
): Promise<ImageCropApiData> {
    return Promise.all(
        sizes.map((size: CreativeImageCropSize) => {
            const cropData = cropDataMap[size.id];

            return getImageCropBlob(size, image, cropData, suggestedCenter);
        })
    );
}

export async function constructImageCropRequestData(
    image: HTMLImageElement,
    cropSizes: CreativeImageCropSize[],
    cropData: CreativeImageCropSizesData,
    isEditMode?: boolean,
    suggestedCenter?: CreativeImageCenter
): Promise<ImageCropRequestData> {
    const sizes = getEditedSizes(cropSizes, cropData, isEditMode);

    const data = await getCroppedImageBlobList(sizes, image, cropData, suggestedCenter);

    return {
        data,
        sizes
    };
}
